import { FC, useEffect, useState } from 'react'

import { RenderIf } from 'components/RenderIf'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { qrMenuGET } from 'featureSliced/shared/api/qrMenu/GET'
import { IProductInfo } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'

import { RichRuns } from './RichRuns'
import { NutrientTable } from './NutrientTable'
import {
  InfoSection,
  InfoSectionTitle,
  ProductInfoContainer,
  StatusText,
  WeightDisclaimer,
} from './styled'

type ProductInfoModalProps = {
  categoryId: number
  productId: number
  unitSellCode?: number | null
}

// QRM-640: sections are rendered from a small config so future fields
// ("zu konsumieren bis" etc.) slot in without touching the layout.
export const ProductInfoModal: FC<ProductInfoModalProps> = ({
  categoryId,
  productId,
  unitSellCode,
}) => {
  const t = useTranslations()

  const [info, setInfo] = useState<IProductInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)

  useEffect(() => {
    let isMounted = true
    qrMenuGET
      .productInfo(categoryId, productId)
      .then((data) => {
        if (!isMounted) return
        setInfo(data)
        setIsLoading(false)
      })
      .catch(() => {
        if (!isMounted) return
        setIsError(true)
        setIsLoading(false)
      })
    return () => {
      isMounted = false
    }
  }, [categoryId, productId])

  const sections = [
    {
      key: 'ingredients',
      title: t('productInfo.ingredients'),
      isVisible: Boolean(info?.declaration?.length),
      content: <RichRuns runs={info?.declaration || []} />,
    },
    {
      key: 'nutrients',
      title: t('productInfo.nutrients'),
      isVisible: Boolean(info?.nutrients?.length),
      content: <NutrientTable rows={info?.nutrients || []} />,
    },
  ]

  return (
    <ProductInfoContainer data-testid="product_info_modal">
      <RenderIf condition={unitSellCode === 2 || unitSellCode === 3}>
        <WeightDisclaimer>{t('products.cutDisclaimer')}</WeightDisclaimer>
      </RenderIf>
      <RenderIf condition={isLoading}>
        <StatusText>{t('common.loading')}</StatusText>
      </RenderIf>
      <RenderIf condition={isError}>
        <StatusText>{t('productInfo.loadError')}</StatusText>
      </RenderIf>
      {sections
        .filter((section) => section.isVisible)
        .map((section) => (
          <InfoSection key={section.key}>
            <InfoSectionTitle>{section.title}</InfoSectionTitle>
            {section.content}
          </InfoSection>
        ))}
    </ProductInfoContainer>
  )
}
